import { useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, RefreshCw } from "lucide-react";

const money = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);
const TONE = {
  CALCULATED: "border-status-available/40 bg-[#3E6B48]/5", OVERRIDDEN: "border-[#D4A373]/50 bg-[#D4A373]/10",
  EXEMPT: "border-border bg-sand/40", CONFIRMATION_REQUIRED: "border-[#D4A373] bg-[#D4A373]/15", MISSING_ADDRESS: "border-border bg-sand/40",
};

export function ProposalTaxPanel({ proposal, editable, onChange }) {
  const t = proposal.tax_detail;
  const [mode, setMode] = useState(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!t) return null;

  const run = async (action, extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dealer/proposals/${proposal.id}/tax`, { action, reason: reason || undefined, ...extra });
      onChange(data); setMode(null); setAmount(""); setReason("");
      toast.success(action === "recalculate" ? "Sales tax recalculated" : "Sales tax updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const needsReason = mode && !reason.trim();

  return (
    <div className={`rounded-md border p-3 text-sm ${TONE[t.status] || "border-border"}`} data-testid="proposal-tax-panel" data-tax-status={t.status}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium text-charcoal" data-testid="proposal-tax-label">{t.label}</p>
          {t.destination && <p className="text-xs text-warmgrey">Destination: {[t.destination.city, t.destination.state, t.destination.postal_code].filter(Boolean).join(", ")} · via Stripe Tax</p>}
          {t.confirmation_reason && <p className="mt-1 text-xs text-[#8a6435]" data-testid="proposal-tax-reason">{t.confirmation_reason}</p>}
          {proposal.tax_stale && <p className="mt-1 text-xs text-[#8a6435]" data-testid="proposal-tax-stale">Inputs changed since this calculation — save to recalculate.</p>}
          {t.override && <p className="mt-1 text-xs text-warmgrey">Corrected by {t.override.by}: “{t.override.reason}” (calculated {money(t.override.replaced?.amount_usd)})</p>}
          {t.exemption && <p className="mt-1 text-xs text-warmgrey">Exempt: {t.exemption.reason}</p>}
        </div>
        <span className="num shrink-0 font-semibold text-charcoal" data-testid="proposal-tax-amount">
          {["CALCULATED", "OVERRIDDEN", "EXEMPT"].includes(t.status) ? money(t.amount_usd) : "Pending"}
        </span>
      </div>
      {(t.breakdown || []).filter((r) => r.taxability_reason === "standard_rated").length > 0 && (
        <ul className="mt-2 space-y-0.5 text-xs text-warmgrey" data-testid="proposal-tax-breakdown">
          {t.breakdown.filter((r) => r.taxability_reason === "standard_rated").map((r, i) => (
            <li key={i} className="flex justify-between"><span>{r.label} on {money(r.taxable_usd)}</span><span className="num">{money(r.amount_usd)}</span></li>
          ))}
          {t.shipping_tax_usd != null && <li className="flex justify-between"><span>Shipping tax</span><span className="num">{money(t.shipping_tax_usd)}</span></li>}
        </ul>
      )}
      {editable && (
        <div className="mt-3 border-t border-border/60 pt-3">
          {!mode ? (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" disabled={busy} onClick={() => run("recalculate")} data-testid="proposal-tax-recalc" className="h-8 border-limestone text-xs"><RefreshCw className="mr-1 h-3 w-3" /> Recalculate</Button>
              {t.status === "CONFIRMATION_REQUIRED" && t.estimate_amount_usd != null && (
                <Button size="sm" disabled={busy} onClick={() => setMode("accept_estimate")} data-testid="proposal-tax-accept" className="h-8 bg-terracotta text-xs text-white hover:bg-terracotta-hover">Accept estimate {money(t.estimate_amount_usd)}</Button>
              )}
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setMode("override")} data-testid="proposal-tax-override" className="h-8 border-limestone text-xs">Correct tax amount</Button>
              {t.status !== "EXEMPT" ? (
                <Button size="sm" variant="outline" disabled={busy} onClick={() => setMode("exempt")} data-testid="proposal-tax-exempt" className="h-8 border-limestone text-xs">Mark customer exempt</Button>
              ) : (
                <Button size="sm" variant="outline" disabled={busy} onClick={() => run("clear", { reason: "exemption removed" })} data-testid="proposal-tax-clear" className="h-8 border-limestone text-xs">Remove exemption</Button>
              )}
            </div>
          ) : (
            <div className="space-y-2" data-testid="proposal-tax-form">
              {mode === "override" && (
                <div><Label className="text-xs text-warmgrey">Corrected tax amount $</Label>
                  <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="proposal-tax-override-amount" className="mt-1 h-9 bg-white" /></div>
              )}
              <div><Label className="text-xs text-warmgrey">Reason (recorded in the audit trail)</Label>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={mode === "exempt" ? "e.g. Resale certificate FL-123456 on file" : "e.g. Customer picks up in-store; county rate differs"} data-testid="proposal-tax-reason-input" className="mt-1 h-9 bg-white" /></div>
              <div className="flex gap-2">
                <Button size="sm" disabled={busy || needsReason || (mode === "override" && amount === "")}
                  onClick={() => run(mode, mode === "override" ? { amount_usd: parseFloat(amount) } : {})} data-testid="proposal-tax-confirm" className="h-8 bg-terracotta text-xs text-white hover:bg-terracotta-hover">
                  {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Confirm"}
                </Button>
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => setMode(null)} data-testid="proposal-tax-cancel" className="h-8 text-xs">Cancel</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
